import { useInternalRef } from '@/shared/hooks/useInternalRef.ts';
import { cn } from '@/shared/utils/cn.ts';
import { PlusIcon, MinusIcon } from '@schuettflix/icons-react';
import React, { forwardRef, useCallback, useEffect, useId, useRef } from 'react';
import { Controller, UseControllerProps, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { SUPPORTED_UNIT_LABEL } from '@/phrase/SupportedUnitLabels.ts';

type OnPropsWithPick<T, P extends keyof T> = {
    [K in keyof T as K extends `on${string}`
        ? K extends 'onChange' | 'onInput'
            ? never
            : K
        : K extends P
          ? K
          : never]: T[K];
};
type NumberInputWithButtonsProps = OnPropsWithPick<
    React.ComponentPropsWithoutRef<'input'>,
    'className' | 'id' | 'value' | 'max' | 'min' | 'step'
> & {
    unit?: string;
    isInvalid?: boolean;
    errorMessage?: string;
    onInput?: (value: number) => void;
    onChange?: (value: number) => void;
};

function forbidNegativeInput(inputEl: HTMLInputElement) {
    if (inputEl.value.startsWith('-')) {
        inputEl.value = inputEl.value.replace('-', '');
    }

    // Incase the user has just typed '-' then no value is set, even though the input event is fired
    // therefore we set the value to '' again and ensure the input is really empty
    if (inputEl.value === '') {
        inputEl.value = '';
    }
}

function forbidDecimalInput(inputEl: HTMLInputElement, step: number) {
    const cleanedVal = inputEl.value.replace(/[^\d.-]/g, '');
    const valueAsNumber = Number(cleanedVal);

    if (isNaN(valueAsNumber)) {
        inputEl.value = '0';
        return;
    }

    if (valueAsNumber % step !== 0) {
        inputEl.value = String(Math.round(valueAsNumber / step) * step);
    }
}

function forbidTooManyDigitsInput(inputEl: HTMLInputElement, max: number) {
    const cleanedVal = inputEl.value.replace(/[^\d.-]/g, '');

    if (Number(cleanedVal) > max) {
        inputEl.value = String(max);
    }
}

const NumberInputWithButtons = forwardRef<HTMLInputElement, NumberInputWithButtonsProps>(
    ({ id, className, value = 0, unit, onInput, onBlur, onChange, isInvalid, errorMessage, ...props }, ref) => {
        const inputMirrorRef = useRef<HTMLSpanElement>(null);
        const inputRef = useInternalRef(ref);

        const randomId = useId();
        const inputId = id ?? `transport-input-${randomId}`;

        const resizeInput = useCallback((inputEl: HTMLInputElement) => {
            if (inputMirrorRef.current) {
                inputMirrorRef.current.textContent = inputEl.value || '0';
                inputEl.style.maxWidth = `${inputMirrorRef.current.offsetWidth}px`;
            }
        }, []);

        const ensureAtLeastAZeroInput = useCallback(
            (inputEl: HTMLInputElement) => {
                if (inputEl && inputEl.value === '') {
                    inputEl.value = '0';
                    resizeInput(inputEl);
                }
            },
            [resizeInput]
        );

        useEffect(() => {
            const inputEl = inputRef.current;
            if (!inputEl) return;
            resizeInput(inputEl);
        }, [value, resizeInput, inputRef]);

        return (
            <div className="grid gap-2">
                <div className={cn('group flex w-full', className)}>
                    <button
                        type="button"
                        className={cn(
                            'disabled:bg-surface-disabled group-focus-within:border-y-active group-focus-within:border-l-active group-hover:border-y-hovered group-hover:border-l-hovered hover:border-hovered disabled:border-disabled hover:bg-surface-hovered focus:border-active focus:bg-surface-hovered flex h-[44px] flex-none items-center rounded-l border px-4 focus:outline-none disabled:cursor-not-allowed',
                            {
                                'border-l-critical border-y-critical disabled:border-l-critical disabled:border-y-critical':
                                    isInvalid,
                            }
                        )}
                        disabled={value == 0}
                        onClick={e => {
                            e.preventDefault();
                            onInput?.(Number(value) - 1);
                            onChange?.(Number(value) - 1);
                        }}
                    >
                        <MinusIcon disabled={value === props.min} />
                    </button>
                    <label
                        className={cn(
                            'font-numbers-md group-hover:border-hovered group-focus-within:border-active flex flex-1 cursor-text items-center justify-center gap-2 border-y px-2',
                            {
                                'border-y-critical': isInvalid,
                            }
                        )}
                        htmlFor={inputId}
                    >
                        <input
                            ref={inputRef}
                            id={inputId}
                            value={value}
                            type="number"
                            className="font-inherit box-content w-full flex-none flex-shrink text-base outline-none"
                            onInput={e => {
                                if (Number(props.min) >= 0) {
                                    forbidNegativeInput(e.currentTarget);
                                }
                                if (Number(props.step) % 1 === 0) {
                                    forbidDecimalInput(e.currentTarget, Number(props.step));
                                }
                                if (props.max) {
                                    forbidTooManyDigitsInput(e.currentTarget, Number(props.max));
                                }
                                resizeInput(e.currentTarget);
                                onInput?.(e.currentTarget.valueAsNumber);
                            }}
                            onBlur={e => {
                                const originalValue = e.currentTarget.value;
                                ensureAtLeastAZeroInput(e.currentTarget);

                                if (originalValue === e.currentTarget.value) {
                                    onBlur?.(e);
                                    return;
                                }

                                const focusEvent: React.FocusEvent<HTMLInputElement> = {
                                    ...e,
                                    target: e.currentTarget,
                                };
                                onBlur?.(focusEvent);
                                onInput?.(e.currentTarget.valueAsNumber);
                                onChange?.(e.currentTarget.valueAsNumber);
                            }}
                            onChange={e => onChange?.(e.currentTarget.valueAsNumber)}
                            {...props}
                        />
                        <span
                            ref={inputMirrorRef}
                            className="font-inherit invisible absolute left-0 top-0 whitespace-pre text-base"
                        />
                        {unit && <span className="font-copy-md">{unit}</span>}
                    </label>
                    <button
                        type="button"
                        className={cn(
                            'disabled:bg-surface-disabled group-focus-within:border-y-active group-focus-within:border-r-active group-hover:border-y-hovered group-hover:border-r-hovered disabled:border-disabled hover:border-hovered hover:bg-surface-hovered focus:border-active focus:bg-surface-hovered flex h-[44px] items-center rounded-r border px-4 focus:outline-none disabled:cursor-not-allowed',
                            {
                                'border-r-critical border-y-critical disabled:border-r-critical disabled:border-y-critical':
                                    isInvalid,
                            }
                        )}
                        disabled={value === props.max}
                        onClick={e => {
                            e.preventDefault();
                            onInput?.(Number(value) + 1);
                            onChange?.(Number(value) + 1);
                        }}
                    >
                        <PlusIcon disabled={value === props.max} />
                    </button>
                </div>
                {errorMessage && <span className="font-copy-sm text-critical">{errorMessage}</span>}
            </div>
        );
    }
);

type TransportQuantityInputProps = {
    transportQuantityInputProps?: Partial<Pick<UseControllerProps, 'name' | 'defaultValue'>> & {
        testId?: string;
        onTransportQuantityChange?: (value?: number) => void;
    };
    className?: string;
};

export const TRANSPORT_QUANTITY_DEFAULT_NAME = 'transportQuantity';

export function TransportQuantityInput({ transportQuantityInputProps }: TransportQuantityInputProps) {
    const { control } = useFormContext();
    const { t } = useTranslation();

    const quantityFieldName = transportQuantityInputProps?.name || TRANSPORT_QUANTITY_DEFAULT_NAME;

    return (
        <Controller
            name={quantityFieldName}
            defaultValue={transportQuantityInputProps?.defaultValue}
            control={control}
            render={({ field: { onChange, value, ...field }, fieldState }) => (
                <NumberInputWithButtons
                    value={value}
                    onChange={value => {
                        onChange(value);
                        transportQuantityInputProps?.onTransportQuantityChange?.(value);
                    }}
                    max={999}
                    min={0}
                    step={1}
                    unit={t(SUPPORTED_UNIT_LABEL.TRANSPORT)}
                    {...field}
                    isInvalid={!!fieldState.error}
                    errorMessage={fieldState.error?.message}
                />
            )}
        />
    );
}
